// NewsletterList.js
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Search, Calendar, User, Eye, ArrowRight, Edit2, Trash2 } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

const NewsletterList = () => {
  const [newsletters, setNewsletters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchNewsletters();
  }, [selectedCategory]);

  const fetchNewsletters = async () => {
    try {
      let url = `${process.env.REACT_APP_BASE_URL}/api/newsletter/published`;
      const params = new URLSearchParams();
      if (selectedCategory) params.append('category', selectedCategory);
      if (params.toString()) url += `?${params.toString()}`;

      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      const data = await response.json();
      setNewsletters(data);
    } catch (error) {
      console.error('Error fetching newsletters:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this newsletter?')) {
      try {
        const response = await fetch(`${process.env.REACT_APP_BASE_URL}/api/newsletter/${id}`, {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        });

        if (response.ok) {
          fetchNewsletters();
        }
      } catch (error) {
        console.error('Error deleting newsletter:', error);
      }
    }
  };

  const handleEdit = (id) => {
    navigate(`/college/newsletter/edit/${id}`);
  };

  const filteredNewsletters = newsletters.filter(newsletter =>
    newsletter.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    newsletter.subtitle?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8 flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Newsletters</h1>
            <p className="mt-2 text-gray-600">Stay updated with the latest news and announcements</p>
          </div>
          <Link
            to="/college/newsletter/new"
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Create Newsletter
          </Link>
        </div>

        {/* Search and Filters */}
        <div className="mb-8 flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search newsletters..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">All Categories</option>
            <option value="announcement">Announcements</option>
            <option value="event">Events</option>
            <option value="achievement">Achievements</option>
            <option value="department">Department</option>
            <option value="general">General</option>
          </select>
        </div>

        {/* Newsletter Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredNewsletters.map(newsletter => (
            <motion.div
              key={newsletter._id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-xl shadow-sm overflow-hidden hover:shadow-md transition-shadow"
            >
              {newsletter.coverImage && (
                <img
                  src={newsletter.coverImage}
                  alt={newsletter.title}
                  className="w-full h-48 object-cover"
                />
              )}
              <div className="p-6">
                <div className="flex items-center gap-2 mb-4">
                  <span className="px-3 py-1 text-sm text-blue-700 bg-blue-50 rounded-full">
                    {newsletter.category}
                  </span>
                  {newsletter.featured && (
                    <span className="px-3 py-1 text-sm text-yellow-700 bg-yellow-50 rounded-full">
                      Featured
                    </span>
                  )}
                </div>

                <h2 className="text-xl font-bold text-gray-900 mb-2">
                  {newsletter.title}
                </h2>
                {newsletter.subtitle && (
                  <p className="text-gray-600 mb-4">{newsletter.subtitle}</p>
                )}

                <div className="flex items-center text-sm text-gray-500 mb-4">
                  <Calendar size={16} className="mr-2" />
                  {new Date(newsletter.createdAt).toLocaleDateString()}
                  <User size={16} className="ml-4 mr-2" />
                  {newsletter.author.name}
                  <Eye size={16} className="ml-4 mr-2" />
                  {newsletter.viewCount}
                </div>

                {/* Actions */}
                <div className="flex items-center justify-between mt-4">
                  <Link
                    to={`/college/newsletter/${newsletter._id}`}
                    className="inline-flex items-center text-blue-600 hover:text-blue-700"
                  >
                    Read More
                    <ArrowRight size={16} className="ml-2" />
                  </Link>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleEdit(newsletter._id)}
                      className="p-2 text-gray-500 hover:text-blue-600 rounded-full hover:bg-blue-50"
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      onClick={() => handleDelete(newsletter._id)}
                      className="p-2 text-gray-500 hover:text-red-600 rounded-full hover:bg-red-50"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {filteredNewsletters.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-600">No newsletters found</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default NewsletterList;


   
